import React from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Divider,
  useTheme,
  Chip,
} from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import TimeAgo from 'react-timeago';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { postContentParser } from './postContentParser';
import formatSpaceDuration from './formatSpaceDuration';

// Custom time formatter matching app styling
const timeFormatter = (value: number, unit: string, suffix: string) => {
  if (value <= 60 && unit === 'second') {
    return 'Just now';
  }
  let unitPluralized = unit;
  if (value !== 1) {
    unitPluralized += 's';
  }
  return `${value} ${unitPluralized} ${suffix}`;
};

export interface RecentUpdateCardProps {
  item: any;
  onReadMore?: (item: any) => void;
}

const RecentUpdateCard: React.FC<RecentUpdateCardProps> = ({
  item,
  onReadMore,
}) => {
  const theme = useTheme();

  const classRoomsMap = useSelector(
    (state: any) => state.data?.classRooms || {}
  );
  const classRooms = Object.values(classRoomsMap);
  const matchedClassRoom = classRooms.find(
    (room: any) =>
      room.objectId === item?.classRoom || room.id === item?.classRoom
  ) as any;
  const classRoomName = matchedClassRoom?.name;
  const classroomDates = matchedClassRoom
    ? formatSpaceDuration(matchedClassRoom.startDate, matchedClassRoom.endDate)
    : '';

  const { text, imagesCount, filesCount } = postContentParser(item?.content);
  const totalAttachments = imagesCount + filesCount;

  const firstName = item?.user?.firstName || 'User';
  const lastName = item?.user?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const avatarUrl = item?.user?.avatar || '';
  const userInitials = `${firstName.charAt(0)}${lastName.charAt(
    0
  )}`.toUpperCase();
  const feedLabel =
    item?.feedType === 'commonRoom' ? 'Common Room' : 'Announcements';

  const shouldTruncate = text.length > 90;
  const displayText = shouldTruncate ? `${text.slice(0, 90)}...` : text;

  const reactionsCount =
    item?._reactionsCount ??
    (item?.reactions?.reduce(
      (sum: number, r: any) =>
        sum + (typeof r.count === 'number' ? r.count : 1),
      0
    ) ||
      0);
  const commentsCount = item?._commentsCount ?? (item?.comments?.length || 0);

  const formattedDate = item?.createdAt
    ? new Date(item.createdAt).toLocaleDateString()
    : '';
  const accessibilityLabel = `${fullName} posted in ${
    classRoomName || feedLabel
  } on ${formattedDate}. ${displayText}. ${
    totalAttachments > 0
      ? `Has ${totalAttachments} attachment${totalAttachments > 1 ? 's' : ''}.`
      : ''
  } ${reactionsCount} reaction${
    reactionsCount !== 1 ? 's' : ''
  }, ${commentsCount} comment${commentsCount !== 1 ? 's' : ''}.`;

  return (
    <Card
      role="article"
      tabIndex={0}
      aria-label={accessibilityLabel}
      sx={{
        borderRadius: 6,
        padding: 2.5,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? '#363636' : '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        boxSizing: 'border-box',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <Chip
        label={feedLabel}
        sx={{
          bgcolor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        }}
      />

      <Box display="flex" alignItems="center" gap={1.5}>
        <Avatar
          src={avatarUrl}
          fallbackText={userInitials}
          sx={{ width: 40, height: 40 }}
          aria-hidden="true"
        />
        <Box>
          <Typography variant="body1" color="textPrimary">
            {fullName}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            <TimeAgo date={item?.createdAt} formatter={timeFormatter} />
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          height: '141px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            maxHeight: '77px',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="body1"
            color="textPrimary"
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {displayText}
            {shouldTruncate && totalAttachments === 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onReadMore?.(item);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onReadMore?.(item);
                  }
                }}
                aria-label={`Read more of ${fullName}'s update`}
                style={{
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  fontWeight: 600,
                  marginLeft: '6px',
                  textDecoration: 'underline',
                  display: 'inline-block',
                }}
              >
                Readmore
              </span>
            )}
          </Typography>
        </Box>

        {totalAttachments > 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ color: theme.palette.text.secondary, height: '24px' }}
          >
            <AttachFileIcon
              fontSize="small"
              color="inherit"
              aria-hidden="true"
            />
            <Typography
              variant="body2"
              color="inherit"
              sx={{ lineHeight: 1.43 }}
            >
              {totalAttachments} attachment{totalAttachments > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {totalAttachments > 0 && (
          <Box sx={{ height: '24px' }}>
            <Typography
              variant="body1"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onReadMore?.(item);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onReadMore?.(item);
                }
              }}
              aria-label={`Read more of ${fullName}'s update`}
              sx={{
                color: theme.palette.primary.main,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 600,
                lineHeight: 1.6,
                display: 'inline-block',
              }}
            >
              Readmore
            </Typography>
          </Box>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={3} mt={0.5}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ color: theme.palette.text.secondary }}
        >
          <SentimentSatisfiedAltIcon
            fontSize="small"
            color="inherit"
            aria-hidden="true"
          />
          <Typography variant="body2" color="inherit">
            {reactionsCount}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ color: theme.palette.text.secondary }}
        >
          <ChatBubbleOutlineIcon
            fontSize="small"
            color="inherit"
            aria-hidden="true"
          />
          <Typography variant="body2" color="inherit">
            {commentsCount}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: theme.palette.divider }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography
          noWrap
          variant="caption"
          color="textSecondary"
          fontWeight={500}
        >
          {classRoomName}
        </Typography>
        {classroomDates && (
          <Typography variant="caption" color="textSecondary" fontWeight={500}>
            {classroomDates}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default RecentUpdateCard;
